import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { DataTable, DataTableColumn } from '../../../components/ui/data-table';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Percent, Calendar, Download, Filter } from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';
import CommissionSummaryCard from '../../../components/trading/CommissionSummaryCard';

type Commission = {
  id: string;
  managerId: string;
  managerName: string;
  period: string;
  totalProfit: number;
  commissionRate: number;
  commissionAmount: number;
  status: 'pending' | 'paid' | 'failed';
  paidDate?: string;
};

const MAMCommissions = () => {
  const commissions: Commission[] = [
    {
      id: 'MCOM001',
      managerId: 'MGR001',
      managerName: 'John Smith',
      period: 'January 2024',
      totalProfit: 46250,
      commissionRate: 15,
      commissionAmount: 6937.5,
      status: 'paid',
      paidDate: '2024-02-01',
    },
    {
      id: 'MCOM002',
      managerId: 'MGR002',
      managerName: 'Jane Doe',
      period: 'January 2024',
      totalProfit: 41040,
      commissionRate: 12,
      commissionAmount: 4924.8,
      status: 'pending',
    },
    {
      id: 'MCOM003',
      managerId: 'MGR003',
      managerName: 'Bob Johnson',
      period: 'January 2024',
      totalProfit: 11590,
      commissionRate: 18,
      commissionAmount: 2086.2,
      status: 'pending',
    },
  ];

  const columns: DataTableColumn<Commission>[] = [
    {
      id: 'id',
      header: 'Commission ID',
      accessor: row => <span className="font-medium">{row.id}</span>,
      sortable: true,
      sortValue: row => row.id,
      width: '140px',
    },
    {
      id: 'managerName',
      header: 'Manager',
      accessor: row => row.managerName,
      sortable: true,
      sortValue: row => row.managerName,
      width: '180px',
    },
    {
      id: 'period',
      header: 'Period',
      accessor: row => row.period,
      sortable: true,
      sortValue: row => row.period,
      width: '140px',
    },
    {
      id: 'totalProfit',
      header: 'Total Profit',
      accessor: row => `$${row.totalProfit.toLocaleString()}`,
      sortable: true,
      sortValue: row => row.totalProfit,
      width: '140px',
    },
    {
      id: 'commissionRate',
      header: 'Rate',
      accessor: row => (
        <div className="flex items-center gap-1">
          <Percent className="h-4 w-4" />
          <span className="font-semibold">{row.commissionRate}%</span>
        </div>
      ),
      sortable: true,
      sortValue: row => row.commissionRate,
      width: '100px',
    },
    {
      id: 'commissionAmount',
      header: 'Commission',
      accessor: row => (
        <span className="text-green-600 font-semibold">
          ${row.commissionAmount.toLocaleString()}
        </span>
      ),
      sortable: true,
      sortValue: row => row.commissionAmount,
      width: '140px',
    },
    {
      id: 'status',
      header: 'Status',
      accessor: row => (
        <Badge
          className={
            row.status === 'paid'
              ? 'bg-green-100 text-green-800'
              : row.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
          }
        >
          {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        </Badge>
      ),
      sortable: true,
      sortValue: row => row.status,
      width: '120px',
    },
    {
      id: 'paidDate',
      header: 'Paid Date',
      accessor: row =>
        row.paidDate ? new Date(row.paidDate).toLocaleDateString() : '-',
      sortable: true,
      sortValue: row => row.paidDate || '',
      width: '120px',
    },
  ];

  const totalCommission = commissions.reduce(
    (sum, com) => sum + com.commissionAmount,
    0
  );
  const pendingCommission = commissions
    .filter(com => com.status === 'pending')
    .reduce((sum, com) => sum + com.commissionAmount, 0);
  const paidCommission = commissions
    .filter(com => com.status === 'paid')
    .reduce((sum, com) => sum + com.commissionAmount, 0);
  const avgCommissionRate =
    commissions.length > 0
      ? commissions.reduce((sum, com) => sum + com.commissionRate, 0) /
        commissions.length
      : 0;

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Commission & Fee Management"
          description="Manage MAM manager commissions and fees"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <CommissionSummaryCard
          totalCommission={totalCommission}
          commissionRate={avgCommissionRate}
          pendingCommission={pendingCommission}
          paidCommission={paidCommission}
          period="Current Month"
        />

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Commission Overview</CardTitle>
            <CardDescription>Summary of commission payments</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Total Commissions
                </p>
                <p className="text-2xl font-bold text-blue-600">
                  ${totalCommission.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Paid This Month</p>
                <p className="text-2xl font-bold text-green-600">
                  ${paidCommission.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Pending Payments
                </p>
                <p className="text-2xl font-bold text-yellow-600">
                  ${pendingCommission.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="text-sm text-muted-foreground">Average Rate</p>
                <p className="text-2xl font-bold text-purple-600">
                  {avgCommissionRate.toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Commission Records</CardTitle>
              <CardDescription>
                Detailed commission payment history
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Calendar className="h-4 w-4 mr-2" />
                Date Range
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={commissions}
            searchPlaceholder="Search by manager or commission ID..."
            showColumnVisibility={true}
            showExportButton={false}
            showAddButton={false}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default MAMCommissions;
